/**
 * Frontend Root URL Resolver
 * 
 * Provides utilities for determining the appropriate landing page URL
 * when navigating to root ('/') or when fallback navigation is needed.
 * 
 * This mirrors the backend logic but works with frontend routing and state.
 */

import {buildURL} from './urlBuilder';
import {hasPermission} from './permissions/permissionDebug';

export interface RootUrlOptions {
  userData?: any;
  menuStructure?: any[];
  forceRefresh?: boolean;
}

export class FrontendRootUrlResolver {
    /**
   * Get the appropriate root URL for the current user
   */
    static getRootUrl(options: RootUrlOptions = {}): string {
        const {userData, menuStructure} = options;

        if (!userData) {
            return '/auth/login';
        }

        // Try each fallback option in order
        const fallbackMethods = [
            () => this.tryGeneralStory(userData),
            () => this.tryMyStoriesPage(userData),
            () => this.trySearchPage(userData),
            () => this.tryDiscoverSection(userData, menuStructure),
            () => this.tryFirstAccessibleMenuItem(userData, menuStructure),
            () => this.tryWelcomePage(),
        ];

        for (const method of fallbackMethods) {
            try {
                const url = method();
                if (url) {
                    return url;
                }
            } catch (error) {
                console.warn(`🏠 FrontendRootUrlResolver: ${method.name} failed:`, error);
                continue;
            }
        }

        // Absolute fallback
        console.error('🏠 FrontendRootUrlResolver: All fallback methods failed, using absolute fallback');
        return '/app/home/welcome';
    }

    /**
   * Try to access the general story
   */
    private static tryGeneralStory(userData: any): string | null {
        try {
            // Check if user has access to insights section
            if (!hasPermission('story.special.read', userData?.permissions)) {
                return null;
            }

            return buildURL('specialStoryDetailView', {story_slug: 'general'});
        } catch (error) {
            console.warn('🏠 Failed to build general story URL:', error);
            return null;
        }
    }

    /**
   * Try to redirect to My Stories page
   */
    private static tryMyStoriesPage(userData: any): string | null {
        if (!hasPermission('story.read', userData?.permissions)) {
            return null;
        }

        try {
            return buildURL('myStoriesView') || '/app/discover/my-stories';
        } catch (error) {
            return '/app/discover/my-stories';
        }
    }

    /**
   * Try to redirect to Search page
   */
    private static trySearchPage(userData: any): string | null {
        if (!hasPermission('search.read', userData?.permissions)) {
            return null;
        }

        try {
            return buildURL('searchView') || '/app/discover/search';
        } catch (error) {
            return '/app/discover/search';
        }
    }

    /**
   * Try to redirect to the Discover section
   */
    private static tryDiscoverSection(userData: any, menuStructure?: any[]): string | null {
        if (!menuStructure) {
            return '/app/discover/my-stories';
        }

        // Find the discover section
        const discoverSection = menuStructure.find(section => 
            section.title?.toLowerCase().includes('discover') || 
      section.path?.includes('discover')
        );

        if (discoverSection?.items?.length > 0) {
            const firstItem = discoverSection.items[0];
            if (firstItem.meta?.routes?.list) {
                return firstItem.meta.routes.list;
            }
        }

        return '/app/discover/my-stories';
    }

    /**
   * Try to find the first accessible menu item
   */
    private static tryFirstAccessibleMenuItem(userData: any, menuStructure?: any[]): string | null {
        if (!menuStructure || !userData) {
            return null;
        }

        for (const section of menuStructure) {
            if (!section.items) {continue;}

            for (const item of section.items) {
                // Check if user has access to this item
                if (item.meta?.accessGroups) {
                    const userGroups = userData.groups || [];
                    const hasAccess = item.meta.accessGroups.some((group: string) => 
                        userGroups.includes(group)
                    );
          
                    if (!hasAccess) {continue;}
                }

                // Return the first accessible route
                if (item.meta?.routes?.list) {
                    return item.meta.routes.list;
                }
                if (item.meta?.routes?.base) {
                    return item.meta.routes.base;
                }
            }
        }

        return null;
    }

    /**
   * Last resort: redirect to welcome page
   */
    private static tryWelcomePage(): string {
        return '/app/home/welcome';
    }

    /**
   * Check if the general story is accessible
   */
    static isGeneralStoryAccessible(userData: any): boolean {
        if (!userData) {return false;}
    
        return hasPermission('story.special.read', userData?.permissions);
    }

    /**
   * Get a safe navigation URL for navigate('/') calls
   */
    static getSafeNavigationUrl(options: RootUrlOptions = {}): string {
        const rootUrl = this.getRootUrl(options);
    
        // Ensure we don't create infinite redirects
        if (rootUrl === '/' || rootUrl === '') {
            return '/app/home/welcome';
        }
    
        return rootUrl;
    }
}

/**
 * Hook for React components to get the root URL
 */
export function useRootUrl(options: RootUrlOptions = {}): string {
    return FrontendRootUrlResolver.getRootUrl(options);
}

/**
 * Convenience function for getting root redirect URL
 */
export function getRootRedirectUrl(options: RootUrlOptions = {}): string {
    return FrontendRootUrlResolver.getSafeNavigationUrl(options);
}

/**
 * Check if general story is accessible
 */
export function isGeneralStoryAccessible(userData: any): boolean {
    return FrontendRootUrlResolver.isGeneralStoryAccessible(userData);
}